/**
 * The language selector (REQ-03): every enabled language in its OWN name (never a flag — a
 * flag is a country, and Spanish is not Spain), plus "Automatic", which follows the browser.
 *
 * A native `<select>`: keyboard, screen reader and phone pickers work with no extra code. It
 * needs no sign-in, and changing it re-renders in place, so nothing in progress is lost.
 */
import { useId } from "react";
import { PSEUDO_LOCALE, getLocaleDefinition, type AppLocale } from "@shared/i18n/localeConfig";
import { useI18n } from "../../i18n/i18nContext";

/** The `<option>` value for "follow the browser". Not a locale tag, so it can never collide. */
export const AUTOMATIC_OPTION = "auto";

export interface LanguageSelectorProps {
  /** Extra class for placement (footer, settings row). Styling lives in `theme-i18n.css`. */
  readonly className?: string;
}

export function LanguageSelector({ className }: LanguageSelectorProps) {
  const { t, explicitLocale, automaticLocale, selectableLocales, pseudoAvailable, setLocale, clearLocaleChoice } =
    useI18n();
  const id = useId();
  const automaticName = getLocaleDefinition(automaticLocale)?.nativeName ?? automaticLocale;

  return (
    <div className={`language-selector${className ? ` ${className}` : ""}`}>
      <label htmlFor={id} className="language-selector__label">
        {t("common.language.label")}
      </label>
      <select
        id={id}
        className="language-selector__control"
        value={explicitLocale ?? AUTOMATIC_OPTION}
        onChange={(e) => {
          const next = e.target.value;
          if (next === AUTOMATIC_OPTION) clearLocaleChoice();
          else setLocale(next as AppLocale);
        }}
      >
        <option value={AUTOMATIC_OPTION}>{t("common.language.automatic", { language: automaticName })}</option>
        {selectableLocales.map((l) => (
          <option key={l.code} value={l.code} lang={l.code}>
            {l.nativeName}
          </option>
        ))}
        {pseudoAvailable && (
          <option value={PSEUDO_LOCALE} lang={PSEUDO_LOCALE}>
            {t("common.language.pseudo")}
          </option>
        )}
      </select>
    </div>
  );
}
